import { useMemo } from 'react';
import type { ReactNode } from 'react';
import BannerInfoIcon from '@assets/icons/BannerInfoIcon';
import BannerSuccessIcon from '@assets/icons/BannerSuccessIcon';
import BannerWarningIcon from '@assets/icons/BannerWarningIcon';
import BannerCriticalIcon from '@assets/icons/BannerCriticalIcon';
import CloseIcon from '@assets/icons/CloseIcon';
import styles from './Banner.module.scss';

// ============================================
// TYPES
// ============================================

export type BannerVariant = 'default' | 'success' | 'warning' | 'critical' | 'informational';

/** Props for the Banner component. */
export interface BannerProps {
  /** Banner header/title text */
  title: string;
  /** Visual style variant */
  variant?: BannerVariant;
  /** Optional supporting description text */
  description?: string;
  /** Optional image element (replaces variant icon when provided) */
  image?: ReactNode;
  /** Show dismiss (X) button */
  dismissible?: boolean;
  /** Callback when dismiss button is clicked */
  onDismiss?: () => void;
  /** Primary action button (ReactNode) */
  primaryAction?: ReactNode;
  /** Secondary action button (ReactNode, only shown when primaryAction exists) */
  secondaryAction?: ReactNode;
  /** Additional CSS class */
  className?: string;
}

// ============================================
// CONSTANTS
// ============================================

const VARIANT_ICON_MAP: Record<BannerVariant, () => ReactNode> = {
  default: () => <BannerInfoIcon />,
  success: () => <BannerSuccessIcon />,
  warning: () => <BannerWarningIcon />,
  critical: () => <BannerCriticalIcon />,
  informational: () => <BannerInfoIcon />,
};

const VARIANT_ROLE_MAP: Record<BannerVariant, string | undefined> = {
  default: undefined,
  success: 'status',
  warning: 'alert',
  critical: 'alert',
  informational: undefined,
};

// ============================================
// HELPERS
// ============================================

function buildClassName(variant: BannerVariant, className?: string): string {
  const classes = [styles.banner, styles[variant]];
  if (className) classes.push(className);
  return classes.join(' ');
}

// ============================================
// COMPONENT
// ============================================

/**
 * Banner -- A notification banner for displaying prominent messages with
 * optional description, actions, and dismiss capability.
 *
 * Supports five visual variants (default, success, warning, critical,
 * informational) with corresponding icons. An optional image can replace
 * the variant icon. Action buttons are passed as ReactNode for maximum
 * flexibility.
 *
 * @example
 * // Simple informational banner
 * <Banner title="Your account has been verified." variant="success" />
 *
 * @example
 * // Banner with description, actions, and dismiss
 * <Banner
 *   title="Action required"
 *   description="Please complete your profile to continue."
 *   variant="warning"
 *   dismissible
 *   onDismiss={() => setVisible(false)}
 *   primaryAction={<Button variant="filled" size="sm">Complete</Button>}
 *   secondaryAction={<Button variant="outline" size="sm">Later</Button>}
 * />
 */
function Banner({
  title,
  variant = 'default',
  description,
  image,
  dismissible = false,
  onDismiss,
  primaryAction,
  secondaryAction,
  className,
}: BannerProps) {
  const rootClassName = useMemo(
    () => buildClassName(variant, className),
    [variant, className],
  );

  const role = VARIANT_ROLE_MAP[variant];
  const iconElement = image ?? VARIANT_ICON_MAP[variant]();

  return (
    <div className={rootClassName} role={role}>
      <span className={image ? styles.image : styles.icon} aria-hidden="true">
        {iconElement}
      </span>

      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        {description && <p className={styles.description}>{description}</p>}

        {primaryAction && (
          <div className={styles.actions}>
            {secondaryAction}
            {primaryAction}
          </div>
        )}
      </div>

      {dismissible && (
        <button
          type="button"
          className={styles.dismiss}
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
}

export default Banner;
